import React, {useEffect, useRef, useState} from 'react';
import {CButton, CSpinner} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import AsyncSelect from "react-select/async";
import {getApi} from "../../../apis/backendApis";
import useAlert from "../../../services/useAlert";
import {ItemSelecionado} from "./selectedItemsList";

interface CasoBusca {
    id: number,
    codigo: string | null,
    name: string,
    grupoId?: number | null,
    grupo?: string | null
}

interface GrupoBusca {
    id: number,
    name: string,
    testCases: Array<CasoBusca>
}

interface RespostaBusca {
    testCases: Array<CasoBusca>,
    groups: Array<GrupoBusca>,
    exact: Array<number>,
    notFound: Array<string>
}

interface Opcao {
    value: string,
    label: string,
    tipo: 'caso' | 'grupo',
    caso?: CasoBusca,
    grupo?: GrupoBusca
}

interface Props {
    selecionados: Array<ItemSelecionado>,
    onAdicionar: (casos: Array<ItemSelecionado>) => void,
    onRemover: (id: number | string) => void
}

const DEBOUNCE_MS = 400;

const paraItem = (caso: CasoBusca, grupo?: string | null): ItemSelecionado => ({
    id: caso.id,
    name: caso.name,
    codigo: caso.codigo,
    grupo: caso.grupo ?? grupo ?? null,
});

const opcaoCaso = (caso: CasoBusca): Opcao => ({
    value: 'c:' + caso.id,
    label: (caso.codigo ? caso.codigo + ' — ' : '') + caso.name,
    tipo: 'caso',
    caso,
});

const temVariosCodigos = (texto: string) => texto.split(/[,;\r\n\t]+/).map(t => t.trim()).filter(Boolean).length > 1;

/**
 * Inclusão de casos no plano (#222790): busca por código, nome ou Grupo de Teste.
 * - Enter com um único resultado (ou código exato) adiciona e limpa o campo.
 * - Com vários resultados a lista fica aberta; clicar adiciona (ou tira) sem fechar a lista.
 * - Vários códigos colados (separados por vírgula, ponto e vírgula ou linha) entram de uma vez.
 */
const TestCasePicker = ({selecionados, onAdicionar, onRemover}: Props) => {
    const [showAlert] = useAlert();
    const [texto, setTexto] = useState<string>('');
    const [menuAberto, setMenuAberto] = useState<boolean>(false);
    const [carregandoTodos, setCarregandoTodos] = useState<boolean>(false);
    const [totalCasos, setTotalCasos] = useState<number | null>(null);

    const ultimaBuscaRef = useRef<{termo: string, resposta: RespostaBusca} | null>(null);
    const debounceRef = useRef<number | null>(null);
    const navegouComTecladoRef = useRef<boolean>(false);

    const idsSelecionados = new Set(selecionados.map(item => String(item.id)));

    useEffect(() => {
        getApi().get('/test-case/search-suite', {params: {all: 1}})
            .then(response => setTotalCasos((response.data?.testCases || []).length))
            .catch(() => setTotalCasos(null));
        return () => {
            if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
        };
    }, []);

    const buscar = async (termo: string): Promise<RespostaBusca> => {
        const response = await getApi().get('/test-case/search-suite', {params: {q: termo}});
        const resposta: RespostaBusca = {testCases: [], groups: [], exact: [], notFound: [], ...response.data};
        ultimaBuscaRef.current = {termo, resposta};
        return resposta;
    };

    const montarOpcoes = (resposta: RespostaBusca) => {
        const grupos = resposta.groups.filter(grupo => grupo.testCases.length > 0).map(grupo => ({
            value: 'g:' + grupo.id,
            label: grupo.name,
            tipo: 'grupo' as const,
            grupo,
        }));
        const casos = resposta.testCases.map(opcaoCaso);
        const opcoes = [];
        if (grupos.length) opcoes.push({label: 'Grupos de teste', options: grupos});
        if (casos.length) opcoes.push({label: 'Casos de teste', options: casos});
        return opcoes;
    };

    const carregarOpcoes = (termo: string, callback: (opcoes: any) => void) => {
        if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => {
            buscar(termo.trim())
                .then(resposta => callback(montarOpcoes(resposta)))
                .catch(() => callback([]));
        }, DEBOUNCE_MS);
    };

    const adicionarGrupo = (grupo: GrupoBusca) => {
        const novos = grupo.testCases.filter(caso => !idsSelecionados.has(String(caso.id)));
        onAdicionar(novos.map(caso => paraItem(caso, grupo.name)));
        showAlert({
            msg: novos.length
                ? novos.length + ' caso(s) do grupo "' + grupo.name + '" adicionados.'
                : 'Todos os casos do grupo "' + grupo.name + '" já estão no plano.',
            type: novos.length ? 'success' : 'warning',
            toaster: true,
        });
    };

    const aoMudar = (_: any, meta: any) => {
        const opcao: Opcao | undefined = meta.option;
        if (!opcao) {
            return;
        }
        if (meta.action === 'select-option') {
            if (opcao.tipo === 'grupo' && opcao.grupo) {
                adicionarGrupo(opcao.grupo);
            } else if (opcao.caso) {
                onAdicionar([paraItem(opcao.caso)]);
            }
        } else if (meta.action === 'deselect-option' && opcao.caso) {
            onRemover(opcao.caso.id);
        }
    };

    const adicionarPorEnter = async () => {
        const termo = texto.trim();
        if (!termo) {
            return;
        }
        const resposta = ultimaBuscaRef.current?.termo === termo ? ultimaBuscaRef.current.resposta : await buscar(termo);

        if (temVariosCodigos(termo)) {
            const encontrados = resposta.testCases.filter(caso => !idsSelecionados.has(String(caso.id)));
            onAdicionar(encontrados.map(caso => paraItem(caso)));
            setTexto('');
            showAlert({
                msg: encontrados.length + ' caso(s) adicionados.'
                    + (resposta.notFound.length ? ' Não encontrados: ' + resposta.notFound.join(', ') + '.' : ''),
                type: resposta.notFound.length ? 'warning' : 'success',
                toaster: true,
            });
            return;
        }

        const grupos = resposta.groups.filter(grupo => grupo.testCases.length > 0);
        const exato = resposta.exact.length === 1 ? resposta.testCases.find(caso => caso.id === resposta.exact[0]) : undefined;
        if (exato || (resposta.testCases.length === 1 && grupos.length === 0)) {
            const caso = exato || resposta.testCases[0];
            if (idsSelecionados.has(String(caso.id))) {
                showAlert({msg: (caso.codigo || caso.name) + ' já está no plano.', type: 'warning', toaster: true});
            } else {
                onAdicionar([paraItem(caso)]);
            }
            setTexto('');
            return;
        }
        if (resposta.testCases.length === 0 && grupos.length === 1) {
            adicionarGrupo(grupos[0]);
            setTexto('');
            return;
        }
        if (resposta.testCases.length === 0 && grupos.length === 0) {
            showAlert({msg: 'Nenhum caso encontrado para "' + termo + '".', type: 'warning', toaster: true});
            return;
        }
        // Vários resultados: a lista fica aberta para escolher
        setMenuAberto(true);
    };

    const aoTeclar = (evento: React.KeyboardEvent) => {
        if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
            navegouComTecladoRef.current = true;
            return;
        }
        if (evento.key === 'Escape') {
            setMenuAberto(false);
            return;
        }
        // Com uma opção escolhida pelas setas, o Enter normal do select marca essa opção
        if (evento.key === 'Enter' && !navegouComTecladoRef.current) {
            evento.preventDefault();
            adicionarPorEnter();
        }
    };

    const adicionarTodos = async () => {
        setCarregandoTodos(true);
        try {
            const response = await getApi().get('/test-case/search-suite', {params: {all: 1}});
            const todos: Array<CasoBusca> = response.data?.testCases || [];
            const novos = todos.filter(caso => !idsSelecionados.has(String(caso.id)));
            onAdicionar(novos.map(caso => paraItem(caso)));
            setTotalCasos(todos.length);
            showAlert({msg: novos.length + ' caso(s) adicionados.', type: 'success', toaster: true});
        } catch (error) {
            showAlert({msg: 'Não foi possível carregar os casos de teste.', type: 'danger', toaster: true});
        } finally {
            setCarregandoTodos(false);
        }
    };

    const formatarOpcao = (opcao: Opcao) => {
        if (opcao.tipo === 'grupo' && opcao.grupo) {
            const faltam = opcao.grupo.testCases.filter(caso => !idsSelecionados.has(String(caso.id))).length;
            return (
                <span className={'tester-opcao' + (faltam === 0 ? ' incluido' : '')}>
                    <span className="check">{faltam === 0 && <CIcon name="cil-check"/>}</span>
                    <CIcon name="cil-layers"/>
                    <span className="nome">{opcao.grupo.name}</span>
                    <span className="grupo">
                        {faltam === 0 ? 'todos incluídos' : opcao.grupo.testCases.length + ' casos' + (faltam < opcao.grupo.testCases.length ? ', faltam ' + faltam : '')}
                    </span>
                </span>
            );
        }
        const caso = opcao.caso as CasoBusca;
        const incluido = idsSelecionados.has(String(caso.id));
        return (
            <span className={'tester-opcao' + (incluido ? ' incluido' : '')}>
                <span className="check">{incluido && <CIcon name="cil-check"/>}</span>
                {caso.codigo && <span className="tester-codigo">{caso.codigo}</span>}
                <span className="nome">{caso.name}</span>
                {caso.grupo && <span className="grupo">{caso.grupo}</span>}
            </span>
        );
    };

    // Campo de texto descarta quebras de linha ao colar: códigos copiados de uma planilha viriam grudados
    const aoColar = (evento: React.ClipboardEvent) => {
        const colado = evento.clipboardData.getData('text');
        if (!/[\r\n\t]/.test(colado)) {
            return;
        }
        evento.preventDefault();
        const codigos = colado.split(/[\r\n\t]+/).map(parte => parte.trim()).filter(Boolean).join(', ');
        setTexto(atual => (atual.trim() ? atual.trim() + ', ' : '') + codigos);
        navegouComTecladoRef.current = false;
    };

    const todosIncluidos = totalCasos !== null && totalCasos > 0 && selecionados.length >= totalCasos;

    return (
        <div className="d-flex flex-wrap align-items-start mb-2" style={{gap: '.5rem'}}>
            <div style={{flex: '1 1 280px', minWidth: 0}} onPasteCapture={aoColar}>
                <AsyncSelect
                    isMulti
                    cacheOptions={false}
                    defaultOptions
                    loadOptions={carregarOpcoes}
                    value={selecionados.map(item => opcaoCaso({id: Number(item.id), codigo: item.codigo || null, name: item.name, grupo: item.grupo}))}
                    getOptionValue={(opcao: any) => opcao.value}
                    formatOptionLabel={formatarOpcao}
                    onChange={aoMudar}
                    inputValue={texto}
                    onInputChange={(valor: string, meta: any) => {
                        if (meta.action === 'input-change') {
                            setTexto(valor);
                            navegouComTecladoRef.current = false;
                            setMenuAberto(true);
                        }
                    }}
                    onKeyDown={aoTeclar}
                    menuIsOpen={menuAberto}
                    onFocus={() => setMenuAberto(true)}
                    onBlur={() => setMenuAberto(false)}
                    onMenuOpen={() => setMenuAberto(true)}
                    onMenuClose={() => setMenuAberto(false)}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    controlShouldRenderValue={false}
                    backspaceRemovesValue={false}
                    isClearable={false}
                    placeholder="Código, nome ou grupo de teste — Enter adiciona"
                    noOptionsMessage={() => texto ? 'Nenhum caso ou grupo encontrado' : 'Digite para buscar'}
                    loadingMessage={() => 'Buscando...'}
                    aria-label="Adicionar casos de teste ao plano"
                    // No body: a seção de casos tem rolagem própria e cortaria o menu
                    menuPortalTarget={document.body}
                    styles={{menuPortal: (base: any) => ({...base, zIndex: 1060})}}
                />
                <div className="tester-ajuda mt-1">
                    Para incluir vários de uma vez, cole os códigos (separados por vírgula ou um por linha) e tecle Enter.
                </div>
            </div>
            <CButton color="secondary" variant="outline" onClick={adicionarTodos} disabled={carregandoTodos || todosIncluidos}>
                {carregandoTodos ? <CSpinner size="sm" className="mr-1"/> : <CIcon name="cil-plus" className="mr-1"/>}
                {todosIncluidos ? 'Todos incluídos' : 'Adicionar todos' + (totalCasos !== null ? ' (' + totalCasos + ')' : '')}
            </CButton>
        </div>
    );
}

export default TestCasePicker;
